// Phase 8.9 — AI Compliance Admin Dashboard

import React, { useEffect, useState, useCallback } from "react";
import {
  ShieldCheck, ShieldAlert, RefreshCw, Search, AlertTriangle, Loader2,
  CheckCircle2, XCircle, Edit3, Filter, Sparkles,
} from "lucide-react";
import api, { apiErrorMessage } from "@/lib/api";
import AdminFeatureShell from "@/components/admin/AdminFeatureShell";

const RISK_TONE = {
  low:        "bg-lifted-green/15 text-lifted-green border-lifted-green/30",
  medium:     "bg-amber-400/15 text-amber-200 border-amber-400/30",
  high:       "bg-rose-500/15 text-rose-200 border-rose-500/30",
  restricted: "bg-rose-700/20 text-rose-100 border-rose-600/40",
};
const STATUS_TONE = {
  approved:     "bg-lifted-green/15 text-lifted-green border-lifted-green/30",
  needs_review: "bg-amber-400/15 text-amber-200 border-amber-400/30",
  restricted:   "bg-rose-500/15 text-rose-200 border-rose-500/30",
  blocked:      "bg-rose-700/20 text-rose-100 border-rose-600/40",
};

const US_STATES = ["AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT","VA","WA","WV","WI","WY","DC"];

export default function AdminCompliance() {
  const [summary, setSummary] = useState(null);
  const [records, setRecords] = useState([]);
  const [filters, setFilters] = useState({ status: "", risk: "" });
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [err, setErr] = useState("");
  const [selected, setSelected] = useState(null);
  const [editor, setEditor] = useState(null);
  const [saving, setSaving] = useState(false);

  const loadSummary = useCallback(async () => {
    try { const { data } = await api.get("/admin/compliance/summary"); setSummary(data); }
    catch (e) { setErr(apiErrorMessage(e)); }
  }, []);

  const loadRecords = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/compliance/products", {
        params: {
          status: filters.status || undefined,
          risk:   filters.risk || undefined,
          limit:  300,
        },
      });
      setRecords(data.records || []);
    } catch (e) { setErr(apiErrorMessage(e)); }
    finally { setLoading(false); }
  }, [filters]);

  useEffect(() => { loadSummary(); loadRecords(); }, [loadSummary, loadRecords]);

  const runScan = async () => {
    setScanning(true); setErr("");
    try {
      await api.post("/admin/compliance/scan");
      await Promise.all([loadSummary(), loadRecords()]);
    } catch (e) { setErr(apiErrorMessage(e)); }
    finally { setScanning(false); }
  };

  const rescanOne = async (handle, useAi=false) => {
    try {
      const { data } = await api.post(`/admin/compliance/scan/${handle}`, null, {
        params: { use_ai: useAi },
      });
      setSelected(data.record);
      await loadRecords();
    } catch (e) { setErr(apiErrorMessage(e)); }
  };

  const openEditor = (rec) => {
    setSelected(rec);
    setEditor({
      compliance_status: rec.compliance_status || "needs_review",
      risk_level: rec.risk_level || "medium",
      age_required: rec.age_required || "21+",
      allowed_for_shipping: rec.allowed_for_shipping ?? true,
      allowed_for_same_day: rec.allowed_for_same_day ?? false,
      restricted_states: rec.restricted_states || [],
      restriction_reason: rec.restriction_reason || "",
      recommended_display_warning: rec.recommended_display_warning || "",
    });
  };

  const saveOverride = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      const { data } = await api.patch(`/admin/compliance/${selected.product_handle}`, editor);
      setSelected(data.record);
      setEditor(null);
      await Promise.all([loadSummary(), loadRecords()]);
    } catch (e) { setErr(apiErrorMessage(e)); }
    finally { setSaving(false); }
  };

  const toggleState = (st) => setEditor((e) => ({
    ...e,
    restricted_states: e.restricted_states.includes(st)
      ? e.restricted_states.filter((x) => x !== st)
      : [...e.restricted_states, st],
  }));

  const filtered = records.filter((r) =>
    !q || (r.product_name || "").toLowerCase().includes(q.toLowerCase())
       || (r.product_handle || "").toLowerCase().includes(q.toLowerCase())
  );

  return (
    <AdminFeatureShell
      breadcrumb="LiftedAtTheMoment / Compliance"
      title="AI Compliance"
      description="Automated catalog compliance. AI heuristics flag risk levels and state restrictions; admins approve or override."
      status={summary ? `${summary.scanned}/${summary.total_products} scanned` : null}
      testId="admin-compliance"
    >
      {err && (
        <div className="glass-panel border border-rose-500/30 p-3 text-rose-200 text-sm" data-testid="compliance-error">{err}</div>
      )}

      {/* KPI strip */}
      {summary && (
        <section className="grid grid-cols-2 lg:grid-cols-5 gap-3" data-testid="compliance-summary">
          <Kpi label="Total products"  value={summary.total_products}      icon={ShieldCheck} tone="neutral" testid="kpi-total" />
          <Kpi label="Approved"        value={summary.approved}            icon={CheckCircle2} tone="green"   testid="kpi-approved" />
          <Kpi label="Needs review"    value={summary.needs_review}        icon={AlertTriangle} tone="amber"  testid="kpi-needs" />
          <Kpi label="Restricted"      value={summary.restricted}          icon={ShieldAlert}  tone="rose"    testid="kpi-restricted" />
          <Kpi label="Blocked"         value={summary.blocked}             icon={XCircle}      tone="rose"    testid="kpi-blocked" />
        </section>
      )}

      <div className="flex flex-wrap gap-2 items-center">
        <button onClick={runScan} disabled={scanning}
                className="btn-lifted !py-2 !px-4 text-xs inline-flex items-center gap-2 disabled:opacity-50"
                data-testid="compliance-scan-all">
          {scanning ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
          Re-scan entire catalog
        </button>
        <div className="glass-panel px-2 py-1.5 flex items-center gap-1 text-xs">
          <Filter className="h-3 w-3 text-lifted-dim" />
          <select value={filters.status} onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))}
                  className="bg-transparent text-xs focus:outline-none"
                  data-testid="compliance-filter-status">
            <option value="">All statuses</option>
            <option value="approved">Approved</option>
            <option value="needs_review">Needs review</option>
            <option value="restricted">Restricted</option>
            <option value="blocked">Blocked</option>
          </select>
          <select value={filters.risk} onChange={(e) => setFilters((f) => ({ ...f, risk: e.target.value }))}
                  className="bg-transparent text-xs focus:outline-none"
                  data-testid="compliance-filter-risk">
            <option value="">Any risk</option>
            <option value="low">Low</option>
            <option value="medium">Medium</option>
            <option value="high">High</option>
            <option value="restricted">Restricted</option>
          </select>
        </div>
        <div className="glass-panel px-2 py-1.5 flex items-center gap-1 flex-1 min-w-[200px]">
          <Search className="h-3 w-3 text-lifted-dim" />
          <input value={q} onChange={(e) => setQ(e.target.value)}
                 placeholder="Search by name or handle…"
                 className="bg-transparent text-xs focus:outline-none flex-1"
                 data-testid="compliance-search" />
        </div>
      </div>

      {/* Records table + editor */}
      <div className="grid lg:grid-cols-[1fr_420px] gap-4 items-start">
        <div className="glass-panel overflow-hidden" data-testid="compliance-table">
          <div className="overflow-x-auto max-h-[640px]">
            <table className="w-full text-sm">
              <thead className="text-[0.6rem] tracking-[0.22em] font-bebas text-lifted-dim uppercase sticky top-0 bg-lifted-bg">
                <tr className="border-b border-white/5">
                  <th className="px-3 py-2 text-left">Product</th>
                  <th className="px-3 py-2 text-left">Risk</th>
                  <th className="px-3 py-2 text-left">Status</th>
                  <th className="px-3 py-2 text-left">Age</th>
                  <th className="px-3 py-2 text-left">Restricted</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr><td colSpan={6} className="px-3 py-6 text-center text-lifted-dim">Loading…</td></tr>
                )}
                {!loading && filtered.length === 0 && (
                  <tr><td colSpan={6} className="px-3 py-6 text-center text-lifted-dim">No matching records. Run scan to seed.</td></tr>
                )}
                {filtered.map((r) => (
                  <tr key={r.product_handle} onClick={() => openEditor(r)}
                      className={`border-b border-white/5 cursor-pointer hover:bg-white/[0.03] ${selected?.product_handle === r.product_handle ? "bg-white/[0.04]" : ""}`}
                      data-testid={`compliance-row-${r.product_handle}`}>
                    <td className="px-3 py-2 text-xs">
                      <div className="text-lifted-text truncate max-w-[280px]">{r.product_name}</div>
                      <div className="text-[0.6rem] font-mono text-lifted-dim">{r.product_handle}</div>
                    </td>
                    <td className="px-3 py-2"><Chip label={r.risk_level} tone={RISK_TONE[r.risk_level]} /></td>
                    <td className="px-3 py-2"><Chip label={(r.compliance_status || "").replace("_", " ")} tone={STATUS_TONE[r.compliance_status]} /></td>
                    <td className="px-3 py-2 text-[0.65rem] text-lifted-dim">{r.age_required}</td>
                    <td className="px-3 py-2 text-[0.65rem] text-lifted-dim">
                      {(r.restricted_states || []).length === 0 ? <span className="text-lifted-green">—</span> :
                        <span title={(r.restricted_states || []).join(", ")}>{(r.restricted_states || []).length} states</span>}
                    </td>
                    <td className="px-3 py-2">
                      <Edit3 className="h-3.5 w-3.5 text-lifted-dim" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="lg:sticky lg:top-4">
          {!selected ? (
            <div className="glass-panel p-6 text-center text-xs text-lifted-dim">
              <ShieldCheck className="h-6 w-6 mx-auto mb-2 text-lifted-green" />
              Select a product to view or override its compliance verdict.
            </div>
          ) : !editor ? (
            <div className="glass-panel p-5 space-y-3" data-testid="compliance-detail">
              <div>
                <div className="font-bebas text-[0.65rem] tracking-[0.22em] text-lifted-green">VERDICT</div>
                <div className="text-sm text-lifted-text mt-1">{selected.product_name}</div>
                <div className="text-[0.6rem] font-mono text-lifted-dim">{selected.product_handle}</div>
              </div>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <Field label="Risk"><Chip label={selected.risk_level} tone={RISK_TONE[selected.risk_level]} /></Field>
                <Field label="Status"><Chip label={(selected.compliance_status || "").replace("_", " ")} tone={STATUS_TONE[selected.compliance_status]} /></Field>
                <Field label="Age"><span className="text-lifted-text">{selected.age_required}</span></Field>
                <Field label="Confidence"><span className="text-lifted-text">{Math.round((selected.confidence_score || 0) * 100)}%</span></Field>
              </div>
              <Field label="Restriction reason"><span className="text-lifted-dim">{selected.restriction_reason || "—"}</span></Field>
              <Field label="Warning"><span className="text-lifted-dim">{selected.recommended_display_warning || "—"}</span></Field>
              <Field label="Restricted states"><span className="text-lifted-dim">{(selected.restricted_states || []).join(", ") || "None"}</span></Field>
              {selected.ai_notes && <Field label="AI notes"><span className="text-lifted-dim text-[0.65rem]">{selected.ai_notes}</span></Field>}
              <div className="flex gap-2 pt-1">
                <button onClick={() => openEditor(selected)} className="btn-lifted !py-2 !px-3 text-xs inline-flex items-center gap-1.5"
                        data-testid="compliance-edit">
                  <Edit3 className="h-3.5 w-3.5" /> Override
                </button>
                <button onClick={() => rescanOne(selected.product_handle, false)} className="btn-ghost text-xs inline-flex items-center gap-1.5"
                        data-testid="compliance-rescan">
                  <RefreshCw className="h-3.5 w-3.5" /> Re-scan
                </button>
                <button onClick={() => rescanOne(selected.product_handle, true)} className="btn-ghost text-xs inline-flex items-center gap-1.5"
                        title="Use AI (Claude) for deeper analysis"
                        data-testid="compliance-rescan-ai">
                  <Sparkles className="h-3.5 w-3.5" /> AI Scan
                </button>
              </div>
            </div>
          ) : (
            <div className="glass-panel p-5 space-y-3" data-testid="compliance-editor">
              <div className="font-bebas text-[0.65rem] tracking-[0.22em] text-amber-300">OVERRIDE</div>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <SelectField label="Status" value={editor.compliance_status} testid="editor-status"
                  options={["approved","needs_review","restricted","blocked"]}
                  onChange={(v) => setEditor((e) => ({ ...e, compliance_status: v }))} />
                <SelectField label="Risk level" value={editor.risk_level} testid="editor-risk"
                  options={["low","medium","high","restricted"]}
                  onChange={(v) => setEditor((e) => ({ ...e, risk_level: v }))} />
                <SelectField label="Age required" value={editor.age_required} testid="editor-age"
                  options={["none","18+","21+"]}
                  onChange={(v) => setEditor((e) => ({ ...e, age_required: v }))} />
                <ToggleField label="Same-day eligible" checked={editor.allowed_for_same_day} testid="editor-same-day"
                  onChange={(v) => setEditor((e) => ({ ...e, allowed_for_same_day: v }))} />
              </div>
              <div>
                <div className="font-bebas text-[0.6rem] tracking-[0.22em] text-lifted-dim mb-1.5">Restricted states</div>
                <div className="flex flex-wrap gap-1 max-h-32 overflow-y-auto" data-testid="editor-states">
                  {US_STATES.map((st) => (
                    <button key={st} onClick={() => toggleState(st)}
                            className={`text-[0.65rem] font-mono px-1.5 py-0.5 rounded border ${
                              editor.restricted_states.includes(st)
                                ? "bg-rose-500/20 text-rose-200 border-rose-500/40"
                                : "border-white/10 text-lifted-dim hover:text-white"
                            }`}>
                      {st}
                    </button>
                  ))}
                </div>
              </div>
              <Input label="Restriction reason" value={editor.restriction_reason}
                     onChange={(v) => setEditor((e) => ({ ...e, restriction_reason: v }))}
                     testid="editor-reason" />
              <Input label="Display warning" value={editor.recommended_display_warning}
                     onChange={(v) => setEditor((e) => ({ ...e, recommended_display_warning: v }))}
                     testid="editor-warning" />
              <div className="flex gap-2 pt-1">
                <button onClick={saveOverride} disabled={saving}
                        className="btn-lifted !py-2 !px-4 text-xs inline-flex items-center gap-1.5 disabled:opacity-50"
                        data-testid="compliance-save">
                  {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5" />}
                  Save override
                </button>
                <button onClick={() => setEditor(null)} className="btn-ghost text-xs" data-testid="compliance-cancel">Cancel</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </AdminFeatureShell>
  );
}

function Kpi({ label, value, icon: Icon, tone, testid }) {
  const toneCls = {
    green:   "text-lifted-green",
    amber:   "text-amber-300",
    rose:    "text-rose-300",
    neutral: "text-white",
  }[tone] || "text-white";
  return (
    <div className="glass-panel p-4" data-testid={testid}>
      <div className="flex items-center gap-2 text-[0.65rem] tracking-[0.22em] font-bebas text-lifted-dim uppercase">
        <Icon className={`h-3.5 w-3.5 ${toneCls}`} /> {label}
      </div>
      <div className={`font-anton text-3xl mt-2 ${toneCls}`}>{value}</div>
    </div>
  );
}

function Chip({ label, tone }) {
  return (
    <span className={`inline-flex items-center gap-1 text-[0.6rem] font-bebas uppercase tracking-[0.18em] border rounded-full px-2 py-0.5 ${tone || ""}`}>
      {label || "—"}
    </span>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div className="font-bebas text-[0.6rem] tracking-[0.22em] text-lifted-dim mb-1">{label}</div>
      <div>{children}</div>
    </div>
  );
}

function SelectField({ label, value, options, onChange, testid }) {
  return (
    <Field label={label}>
      <select value={value} onChange={(e) => onChange(e.target.value)}
              className="w-full bg-black/40 border border-white/10 rounded-md px-2 py-1.5 text-xs focus:outline-none"
              data-testid={testid}>
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </Field>
  );
}

function ToggleField({ label, checked, onChange, testid }) {
  return (
    <Field label={label}>
      <button onClick={() => onChange(!checked)}
              className={`px-3 py-1 rounded-md border text-xs ${checked ? "bg-lifted-green/15 text-lifted-green border-lifted-green/40" : "border-white/10 text-lifted-dim"}`}
              data-testid={testid}>
        {checked ? "Yes" : "No"}
      </button>
    </Field>
  );
}

function Input({ label, value, onChange, testid }) {
  return (
    <Field label={label}>
      <input value={value || ""} onChange={(e) => onChange(e.target.value)}
             className="w-full bg-black/40 border border-white/10 rounded-md px-2 py-1.5 text-xs focus:outline-none"
             data-testid={testid} />
    </Field>
  );
}
